// 請われぬ介錯の技の音 (2026-10-29・音の専用セッション・記録 = docs/research/2026-10-29-executioner-sfx.md)。
//   ユーザー原文 (10-29)「解釈専用の音を全く作っていない」「解釈の音は基本的に独自のものじゃなくて、だいたい元からAIが合成音で入れてくれてるやつ」
//   (「解釈」は介錯の聞き違い)。それまでは全ボス共通の唸り (telegraph_growl)・機械音 (telegraph_whirr)・共通の足音 (step) だけだった。
//   予告は体と得物が立てる音 (大剣を担ぐ鉄・砲の機構・炎を吸う・息を吸う)。露骨な合図の層は足さない。
//   名前が exec_sfx_ で始まる = ボスの技の音 (src/sound_priority.ts)。ただし予告 (_tell_) はプレイヤーの音で下げない (予告が消えない)。
//   既定 e = 配布版 (11-03)。開発版 ?execSound=a (最初の音) / b (別テイク) / c (10-30・Surge XT のシンセで作った振り下ろし→地揺らしの4役。ほかの役は a のまま) / d (10-30・作者が選んだ手本に寄せたシンセ。風切りと叩きつけの2役だけ・担ぐ予告と地揺らしの溜めは無音) / e (11-03・d の2役に残りの役も Surge XT のシンセでそろえた比較版。刺突・払いの予告も無音) / off (前の音 = 共通の唸りと足音)。URL は game.ts の DEV の箱の中でだけ読む。
//   素材 = scripts/render_exec_sfx.py → public/sfx/exec_sfx_<a|b>_<役>.wav (a は配布版の preloadSfxSamples が読む・b は開発版で選んだ時だけ読む)。
import type { SfxId } from './types'
import { execTransitionShout } from './exec_look_1101'

export const EXEC_SOUND_TAKES = ['a', 'b', 'c', 'd', 'e'] as const
export type ExecSoundTake = typeof EXEC_SOUND_TAKES[number]
export type ExecSound = 'off' | ExecSoundTake
export const EXEC_SOUND_ROLES = [
  'tell_light', 'tell_heavy', 'tell_quake', 'tell_cannon', 'tell_fajin', 'tell_roar',
  'swing_s', 'swing_l', 'swing_xl', 'shot', 'mortar', 'fajin', 'roar', 'blast',
  'step', 'morph', 'skid',
] as const
export type ExecSoundRole = typeof EXEC_SOUND_ROLES[number]
// c が持つ役 (シンセの試作・記録 = docs/research/2026-10-30-executioner-sfx-tools.md)。
export const EXEC_SOUND_C_ROLES = ['tell_heavy', 'swing_l', 'tell_quake', 'swing_xl'] as const satisfies readonly ExecSoundRole[]
// d が持つ役と、d で鳴らさない役 (10-30・手本＝docs/research/2026-10-30-answers-batch3.md)。
//   作者原文「予告には基本音がない／魔法系とか飛び道具、掛け声などはある／担ぐ音はいらない」。振り下ろしの当たり・地揺らしの溜めも「いらない」。
//   swing_l＝坩堝の騎士の大剣の縦振りの風切りだけ (当たりの層を持たない)・swing_xl＝星砕きのラダーンの隕石の着地。
export const EXEC_SOUND_D_ROLES = ['swing_l', 'swing_xl'] as const satisfies readonly ExecSoundRole[]
const EXEC_SOUND_D_SILENT: readonly ExecSoundRole[] = ['tell_heavy', 'tell_quake']
// e が持つ役 (11-03・記録 = docs/research/2026-11-03-exec-sfx-e.md・台本 = LUNAIRE-notes の render_exec_e_1103.py)。
//   d に無い役を d と同じ質感 (暗い風・サインの胴・鈍い鉄) のシンセでそろえた。swing_l / swing_xl は d のまま。
//   予告は作者の答えに合わせ、砲の機構 (飛び道具)・発勁の溜め (気の技)・咆哮の溜め (掛け声) だけ鳴らし、刺突・払いの予告も無音にする。
export const EXEC_SOUND_E_ROLES = [
  'tell_cannon', 'tell_fajin', 'tell_roar', 'swing_s', 'shot', 'mortar', 'fajin', 'roar', 'blast', 'step', 'morph', 'skid',
] as const satisfies readonly ExecSoundRole[]
const EXEC_SOUND_E_SILENT: readonly ExecSoundRole[] = ['tell_light', 'tell_heavy', 'tell_quake']
const rolesOf = (k: ExecSoundTake): readonly ExecSoundRole[] =>
  (k === 'c' ? EXEC_SOUND_C_ROLES : k === 'd' ? EXEC_SOUND_D_ROLES : k === 'e' ? EXEC_SOUND_E_ROLES : EXEC_SOUND_ROLES)
export const EXEC_SOUND_SFX_IDS = EXEC_SOUND_TAKES.flatMap(k => rolesOf(k).map(r => `exec_sfx_${k}_${r}` as const))

// 11-03 作者の答え (choice=ship)：残りの役もシンセでそろえた e を配布版の既定にする (振り下ろし・叩きつけは d の音)。開発版 ?execSound=d で前の配布版 (d に無い役は a)。
export const EXEC_SOUND_DEFAULT: ExecSound = 'e'
let take: ExecSound = EXEC_SOUND_DEFAULT
export function getExecSound(): ExecSound { return take }
export function setExecSound(value: string | null | undefined): ExecSound {
  take = value === 'a' || value === 'b' || value === 'c' || value === 'd' || value === 'e' || value === 'off' ? value : EXEC_SOUND_DEFAULT
  if (take !== 'off' && take !== 'a' && take !== 'd' && take !== 'e' && typeof fetch !== 'undefined') void import('./audio').then(m => m.preloadExecSound(take as ExecSoundTake))
  return take
}

// 溜めの頭で鳴らす音 (技の重さと得物)。
const TELL_ROLE: Readonly<Record<string, ExecSoundRole>> = {
  exec_maulJab: 'tell_light', exec_cannonSweep: 'tell_light',
  exec_maulDrop: 'tell_heavy', exec_maulHold: 'tell_heavy', exec_maulSet: 'tell_heavy', executioner_finisher: 'tell_heavy',
  exec_maulQuake: 'tell_quake',
  exec_cannonShot: 'tell_cannon', exec_cannonVolley: 'tell_cannon', exec_cannonMortar: 'tell_cannon',
  exec_cannonFajin: 'tell_fajin', exec_bladeFajin: 'tell_fajin',
  executioner_phaseTransition: 'tell_roar',
}
// 当たりに入った瞬間の音。振りの大きさで3段 (小さい振りは小さく)・砲は発射・咆哮は口を開いた瞬間。
const ACTIVE_ROLE: Readonly<Record<string, ExecSoundRole>> = {
  exec_maulJab: 'swing_s', exec_cannonSweep: 'swing_s', exec_maulSet: 'swing_s',
  exec_maulDrop: 'swing_l', exec_maulHold: 'swing_l',
  exec_maulQuake: 'swing_xl', exec_maulSetPierce: 'swing_xl', executioner_finisher: 'swing_xl',
  exec_cannonShot: 'shot', exec_cannonVolley: 'shot',
  exec_cannonMortar: 'mortar',
  exec_cannonFajin: 'fajin', exec_bladeFajin: 'fajin',
  executioner_phaseTransition: 'roar',
}
export function execTellRoleFor(attackId: string): ExecSoundRole | null { return TELL_ROLE[attackId] ?? null }
// 11-01 作者「2幕に切り替わるときの、殴るときに…ウオーっていう声が入ってるんだけど、それはいらないです」
//   → 幕の切り替わりの斬りかかりは大きな振りの風切りにする。前の声 = DEV ?execShout=roar (src/exec_look_1101.ts)。
export function execActiveRoleFor(attackId: string): ExecSoundRole | null {
  if (attackId === 'executioner_phaseTransition' && !execTransitionShout()) return 'swing_l'
  return ACTIVE_ROLE[attackId] ?? null
}

function idFor(role: ExecSoundRole | null): SfxId | null {
  if (take === 'off' || !role) return null
  let k: ExecSoundTake = take
  if (k === 'e' && !rolesOf('e').includes(role)) k = 'd'
  if ((k === 'c' || k === 'd') && !rolesOf(k).includes(role)) k = 'a'
  return `exec_sfx_${k}_${role}` as SfxId
}
/** 溜めに入っても何も鳴らさない技か (d の担ぐ予告・地揺らしの溜め／e はさらに刺突・払いの予告)。共通の唸りにも戻さない。 */
export function execTellSilent(attackId: string): boolean {
  const role = execTellRoleFor(attackId)
  if (role === null) return false
  return (take === 'd' && EXEC_SOUND_D_SILENT.includes(role)) || (take === 'e' && EXEC_SOUND_E_SILENT.includes(role))
}
/** 溜めに入った時の音。null = 共通の唸りのまま (off・対象外の技)。 */
export function execTellSfx(attackId: string): SfxId | null { return idFor(execTellRoleFor(attackId)) }
/** 当たりに入った時の音。null = 鳴らさない (off・対象外の技)。 */
export function execActiveSfx(attackId: string): SfxId | null { return idFor(execActiveRoleFor(attackId)) }
/** 介錯の一歩・組み変わり・榴弾の着弾・押し飛ばされて滑る音。null = 前の音。 */
export function execBodySfx(role: 'step' | 'morph' | 'blast' | 'skid'): SfxId | null { return idFor(role) }

/** 予告の音か (プレイヤーの音で下げない)。 */
export function isExecTellSfx(id: string): boolean { return id.startsWith('exec_sfx_') && id.includes('_tell_') }

/** 先読みする素材 [キー, URL]。キー = 効果音の名前。 */
export function execSoundFiles(k: ExecSoundTake): [string, string][] {
  return rolesOf(k).map(r => `exec_sfx_${k}_${r}`).map(id => [id, `/sfx/${id}.wav?v=1`])
}
